import React, { useState, useEffect } from 'react';
import { getApiBase } from '../utils/apiConfig';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Card,
  CardContent,
  CardActions,
  Grid,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';

const PREVIEWABLE_EXTENSIONS = new Set(['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp']);
const isPreviewable = (name) => PREVIEWABLE_EXTENSIONS.has(`.${(name || '').split('.').pop().toLowerCase()}`);

function AdminMoverApproval() {
  const [movers, setMovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMover, setSelectedMover] = useState(null);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [commissionRate, setCommissionRate] = useState('');
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(null); // { name, mimeType, blobUrl }
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    fetchMovers();
  }, []);

  const fetchMovers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBase()}/admin/movers`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        setMovers(data);
      }
    } catch (error) {
      console.error('Error fetching movers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalClick = (mover) => {
    setSelectedMover(mover);
    setApprovalStatus(mover.approvalStatus || 'pending');
    setApprovalNotes(mover.approvalNotes || '');
    setCommissionRate(
      mover.commissionOverrideRate === null || mover.commissionOverrideRate === undefined
        ? ''
        : String(Math.round(mover.commissionOverrideRate * 10000) / 100)
    );
    setApprovalDialog(true);
  };

  const handleApprovalSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBase()}/admin/movers/${selectedMover._id}/approval`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          approvalStatus,
          approvalNotes,
          authorized: approvalStatus === 'approved',
          commissionRate: commissionRate === '' ? null : Number(commissionRate),
        }),
      });

      if (response.ok) {
        setMessage(`Mover ${approvalStatus === 'approved' ? 'approved' : 'status updated'} successfully!`);
        fetchMovers(); // Refresh the list
        setApprovalDialog(false);
      } else {
        setMessage('Error updating mover status');
      }
    } catch (error) {
      console.error('Error updating approval:', error);
      setMessage('Error updating mover status');
    }
  };

  const handleDocumentDownload = async (uploadedDocument, documentIndex) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBase()}/admin/movers/${selectedMover._id}/documents/${documentIndex}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Unable to download document');
      }

      const blobUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = uploadedDocument.name;
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const handleDocumentPreview = async (uploadedDocument, documentIndex) => {
    setPreviewLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBase()}/admin/movers/${selectedMover._id}/documents/${documentIndex}?mode=inline`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Unable to preview document');
      }

      const blob = await response.blob();
      setPreview({ name: uploadedDocument.name, mimeType: blob.type, blobUrl: URL.createObjectURL(blob) });
    } catch (error) {
      setMessage(error.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (preview?.blobUrl) URL.revokeObjectURL(preview.blobUrl);
    setPreview(null);
  };

  const getStatusColor = (status, authorized) => {
    if (authorized) return 'success';
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'requires_documents': return 'warning';
      default: return 'default';
    }
  };

  const getStatusText = (mover) => {
    if (mover.authorized) return 'Authorized';
    if (mover.onboardingSkipped) return 'Incomplete (Skipped)';
    return mover.approvalStatus || 'Pending';
  };

  if (loading) {
    return <Typography>Loading movers...</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Mover Approval Management
      </Typography>

      {message && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Movers
              </Typography>
              <Typography variant="h4">
                {movers.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Authorized
              </Typography>
              <Typography variant="h4" color="success.main">
                {movers.filter(m => m.authorized).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" color="warning.main">
                {movers.filter(m => !m.authorized && (m.approvalStatus === 'pending' || !m.approvalStatus)).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Incomplete
              </Typography>
              <Typography variant="h4" color="error.main">
                {movers.filter(m => m.onboardingSkipped).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Movers Table */}
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Company Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Services</TableCell>
              <TableCell>Documents</TableCell>
              <TableCell>Commission</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Registration Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {movers.map((mover) => (
              <TableRow key={mover._id}>
                <TableCell>{mover.name}</TableCell>
                <TableCell>{mover.userId?.email || 'N/A'}</TableCell>
                <TableCell>
                  {mover.services?.map((service) => (
                    <Chip key={service} label={service} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  ))}
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${mover.documents?.length || 0} submitted`}
                    color={mover.documents?.length ? 'success' : 'default'}
                    variant={mover.documents?.length ? 'filled' : 'outlined'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {mover.commissionOverrideRate !== null && mover.commissionOverrideRate !== undefined
                    ? <Chip label={`${Math.round(mover.commissionOverrideRate * 10000) / 100}%`} size="small" color="primary" />
                    : <Chip label="Default" size="small" variant="outlined" />}
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusText(mover)}
                    color={getStatusColor(mover.approvalStatus, mover.authorized)}
                    variant={mover.authorized ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell>
                  {new Date(mover.createdAt || Date.now()).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleApprovalClick(mover)}
                  >
                    Manage
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Manage Mover: {selectedMover?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Submitted Documents
            </Typography>
            {selectedMover?.documents?.length ? (
              <Box sx={{ mb: 3 }}>
                {selectedMover.documents.map((uploadedDocument, documentIndex) => (
                  <Box
                    key={`${uploadedDocument.name}-${documentIndex}`}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{uploadedDocument.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Uploaded {uploadedDocument.uploaded ? new Date(uploadedDocument.uploaded).toLocaleString() : 'date unavailable'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {isPreviewable(uploadedDocument.name) && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          disabled={previewLoading}
                          onClick={() => handleDocumentPreview(uploadedDocument, documentIndex)}
                        >
                          Preview
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDocumentDownload(uploadedDocument, documentIndex)}
                      >
                        Download
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mb: 3 }}>
                No documents have been submitted for this mover.
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Approval Status</InputLabel>
              <Select
                value={approvalStatus}
                onChange={(e) => setApprovalStatus(e.target.value)}
              >
                <MenuItem value="pending">Pending Review</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="requires_documents">Requires Documents</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes"
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              placeholder="Add notes about the approval decision..."
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="number"
              label="Commission Rate (%)"
              value={commissionRate}
              onChange={(e) => setCommissionRate(e.target.value)}
              placeholder="Leave blank to use the default tiered rate"
              helperText="MOVD's cut of each job for this mover. Leave blank to fall back to the standard tiered rate."
              inputProps={{ min: 0, max: 100, step: 0.5 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
          <Button onClick={handleApprovalSubmit} variant="contained">
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={!!preview} onClose={closePreview} maxWidth="md" fullWidth>
        <DialogTitle>{preview?.name}</DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', minHeight: 400 }}>
          {preview?.mimeType === 'application/pdf' ? (
            <Box component="iframe" src={preview.blobUrl} sx={{ width: '100%', height: '70vh', border: 0 }} />
          ) : (
            <Box component="img" src={preview?.blobUrl} alt={preview?.name} sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminMoverApproval;